/*
 * These icons bind their `color` prop to an SVG `fill` / `stroke`
 * presentation attribute. That is fine for a literal or `currentColor`, but
 * a CSS variable does NOT reliably resolve in a presentation attribute in
 * Safari — `<Icon color="var(--color-text-subtle)" />` silently renders
 * black there.
 *
 * To colour one from the design tokens, pass it through `style` instead,
 * which wins over the presentation attribute in the cascade:
 *   <InfoIcon style={{ stroke: 'var(--color-util-text-muted)' }} />
 *
 * Hardening all 40 to apply `color` via `style` would remove the trap; it
 * has not been done because only a handful of call sites need a token.
 */

import React from 'react';

/**
 * Standardized Icon Library
 *
 * This file contains all SVG icons used throughout the application.
 * Icons are implemented as React components for easy reuse and consistency.
 *
 * Usage:
 * import { TrashIcon, CloseIcon } from './icons/Icons';
 * <TrashIcon width={14} height={14} color="white" />
 */

// Trash/Delete Icon (Bootstrap Icons - trash3)
export const TrashIcon = ({
  width = 16,
  height = 16,
  color = 'currentColor',
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 16 16"
    fill={color}
    {...props}
  >
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
    <path
      fillRule="evenodd"
      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
    />
  </svg>
);

// Close/X Icon
export const CloseIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Download Icon
export const DownloadIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

// Chevron Left Icon
export const ChevronLeftIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

// Chevron Right Icon
export const ChevronRightIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

// Eye Icon (View)
export const EyeIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Target Icon (Show in the scene)
export const TargetIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="2" fill={color} />
    <path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
  </svg>
);

// Expand Icon
export const ExpandIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
  </svg>
);

// Check Icon
export const CheckIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Plus Icon
export const PlusIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// Settings Icon (Gear)
export const SettingsIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

// Info Icon
export const InfoIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

// Warning Icon
export const WarningIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />
  </svg>
);

// Home Icon
export const HomeIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <path d="M9 22V12h6v10" />
  </svg>
);

// User Icon
export const UserIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Edit Icon (Bootstrap Icons pencil-fill style, used for rotation/edit fields)
export const EditIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2, // Not used for this filled icon but kept for API compatibility
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 16 16"
    fill={color}
    {...props}
  >
    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
  </svg>
);

// Copy Icon
export const CopyIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

// Search Icon
export const SearchIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// Filter Icon
export const FilterIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
  </svg>
);

// Upload Icon
export const UploadIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  </svg>
);

// Refresh Icon
export const RefreshIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 2,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M1 4v6h6M23 20v-6h-6" />
    <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
  </svg>
);

// Lock / Unlock — thin-line padlock. Unlock shows the shackle pivoted open
// to the right (right side detached from the body, left side still attached).
export const LockIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="5" y="11" width="14" height="10" rx="1.5" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export const UnlockIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/*
      Open padlock: body shifted left to leave room for the shackle to
      land clearly OUTSIDE the body on the right. Left leg of the
      shackle attaches at the body's top-right corner; the arc sweeps
      over and the right leg hangs down with several units of daylight
      between it and the body's right edge.
    */}
    <rect x="3" y="11" width="12" height="10" rx="1.5" />
    <path d="M11 11V7a4.5 4.5 0 0 1 9 0v6" />
  </svg>
);

// Movement Arrow Icons - Modern thin line style
export const ArrowUpIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 3v18M5 10l7-7 7 7" />
  </svg>
);

export const ArrowDownIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 3v18M19 14l-7 7-7-7" />
  </svg>
);

export const ArrowLeftIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 12h18M10 5l-7 7 7 7" />
  </svg>
);

export const ArrowRightIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 12h18M14 5l7 7-7 7" />
  </svg>
);

// Rotation Icons - Chart quadrant style with arcs
export const Rotate30CWIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Chart axes */}
    <path d="M6 18L6 6M6 18L18 18" strokeOpacity="0.3" />
    {/* Arc from bottom to 30 degrees up (clockwise from x-axis) */}
    <path d="M 18 18 A 12 12 0 0 0 16 12" />
    {/* Arrow pointing down and right (tangent to arc end) */}
    <path d="M16 12l1 2M16 12l-2 1" />
    <text
      x="12"
      y="22"
      fontSize="8"
      fontWeight="400"
      textAnchor="middle"
      fill={color}
      stroke="none"
    >
      30°
    </text>
  </svg>
);

export const Rotate30CCWIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Chart axes */}
    <path d="M6 18L6 6M6 18L18 18" strokeOpacity="0.3" />
    {/* Arc from right to 30 degrees up (counter-clockwise from x-axis) */}
    <path d="M 18 18 A 12 12 0 0 1 12 16" />
    {/* Arrow pointing up and left (tangent to arc end) */}
    <path d="M12 16l-2-1M12 16l1-2" />
    <text
      x="12"
      y="22"
      fontSize="8"
      fontWeight="400"
      textAnchor="middle"
      fill={color}
      stroke="none"
    >
      30°
    </text>
  </svg>
);

export const Rotate45CWIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Chart axes */}
    <path d="M6 18L6 6M6 18L18 18" strokeOpacity="0.3" />
    {/* Arc from bottom to 45 degrees (clockwise from x-axis) */}
    <path d="M 18 18 A 12 12 0 0 0 15 9" />
    {/* Arrow pointing down and right at 45 degrees */}
    <path d="M15 9l0 2M15 9l-2 0" />
    <text
      x="12"
      y="22"
      fontSize="8"
      fontWeight="400"
      textAnchor="middle"
      fill={color}
      stroke="none"
    >
      45°
    </text>
  </svg>
);

export const Rotate45CCWIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Chart axes */}
    <path d="M6 18L6 6M6 18L18 18" strokeOpacity="0.3" />
    {/* Arc from right to 45 degrees up (counter-clockwise from x-axis) */}
    <path d="M 18 18 A 12 12 0 0 1 9 15" />
    {/* Arrow pointing up and left at 45 degrees */}
    <path d="M9 15l-2 0M9 15l0 -2" />
    <text
      x="12"
      y="22"
      fontSize="8"
      fontWeight="400"
      textAnchor="middle"
      fill={color}
      stroke="none"
    >
      45°
    </text>
  </svg>
);

export const Rotate90CWIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Curved arrow going clockwise */}
    <path d="M21 12a9 9 0 11-9-9 9 9 0 019 9" />
    <path d="M21 12l-4-4M21 12l-4 4" />
    {/* 90° text in center */}
    <text
      x="12"
      y="16"
      fontSize="10"
      fontWeight="500"
      textAnchor="middle"
      fill={color}
      stroke="none"
    >
      90°
    </text>
  </svg>
);

export const Rotate90CCWIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Curved arrow going counter-clockwise */}
    <path d="M3 12a9 9 0 119 9 9 9 0 01-9-9" />
    <path d="M3 12l4 4M3 12l4-4" />
    {/* 90° text in center */}
    <text
      x="12"
      y="16"
      fontSize="10"
      fontWeight="500"
      textAnchor="middle"
      fill={color}
      stroke="none"
    >
      90°
    </text>
  </svg>
);

// Laser Pointer Icon — a pen body firing a red beam at a red dot. The body
// uses `color` (currentColor by default, so it adapts to surrounding text),
// while the beam + dot are always laser-red so the "laser" reads regardless
// of context color. Used by the Bluetooth laser-measurement UI.
export const LaserIcon = ({
  width = 24,
  height = 24,
  color = 'currentColor',
  beamColor = '#ef4444',
  ...props
}) => {
  // Eight rays radiating from the laser dot — a twinkle / sun burst.
  const cx = 19;
  const cy = 12;
  const rays = Array.from({ length: 8 }, (_, i) => {
    const a = (i * Math.PI) / 4 - Math.PI / 2;
    return {
      x1: (cx + 2.6 * Math.cos(a)).toFixed(2),
      y1: (cy + 2.6 * Math.sin(a)).toFixed(2),
      x2: (cx + 4.9 * Math.cos(a)).toFixed(2),
      y2: (cy + 4.9 * Math.sin(a)).toFixed(2),
    };
  });
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      {...props}
    >
      {/* Pointer body */}
      <rect x="1" y="9.5" width="8" height="5" rx="1.6" fill={color} />
      {/* Emitter tip */}
      <rect x="9" y="10.8" width="1.4" height="2.4" rx="0.5" fill={color} />
      {/* Beam */}
      <path
        d="M11 12H15"
        stroke={beamColor}
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      {/* Radiant twinkle around the dot */}
      {rays.map((r, i) => (
        <line
          key={i}
          x1={r.x1}
          y1={r.y1}
          x2={r.x2}
          y2={r.y2}
          stroke={beamColor}
          strokeWidth="0.8"
          strokeLinecap="round"
        />
      ))}
      {/* Laser dot */}
      <circle cx={cx} cy={cy} r="2" fill={beamColor} />
    </svg>
  );
};

// Export default object for convenience
const Icons = {
  Trash: TrashIcon,
  Close: CloseIcon,
  Download: DownloadIcon,
  ChevronLeft: ChevronLeftIcon,
  ChevronRight: ChevronRightIcon,
  Eye: EyeIcon,
  Expand: ExpandIcon,
  Check: CheckIcon,
  Plus: PlusIcon,
  Settings: SettingsIcon,
  Info: InfoIcon,
  Warning: WarningIcon,
  Home: HomeIcon,
  User: UserIcon,
  Edit: EditIcon,
  Copy: CopyIcon,
  Search: SearchIcon,
  Filter: FilterIcon,
  Upload: UploadIcon,
  Refresh: RefreshIcon,
  Lock: LockIcon,
  Unlock: UnlockIcon,
  ArrowUp: ArrowUpIcon,
  ArrowDown: ArrowDownIcon,
  ArrowLeft: ArrowLeftIcon,
  ArrowRight: ArrowRightIcon,
  Rotate30CW: Rotate30CWIcon,
  Rotate30CCW: Rotate30CCWIcon,
  Rotate45CW: Rotate45CWIcon,
  Rotate45CCW: Rotate45CCWIcon,
  Rotate90CW: Rotate90CWIcon,
  Rotate90CCW: Rotate90CCWIcon,
  Laser: LaserIcon,
};

export default Icons;
